import { z } from 'zod';
import { Account, Entry, MAX_AMOUNT, People } from './schemas.ts';

// Monthly reconciliation and period close (plan 2A Assumptions 7 and 8).

export const Reconciliation = z.object({
  id: z.string(),
  account: z.string(),
  /** YYYY-MM in the bank's time zone. */
  month: z.string(),
  openedAt: z.iso.datetime(),
  dueAt: z.iso.datetime(),
  status: z.enum(['open', 'closed']),
  /** How it closed: confirmed unchanged, reported equal to the bank, or adjusted by the difference. */
  outcome: z.enum(['unchanged', 'matched', 'adjusted']).nullable(),
  /** What the bank showed, and what the holder reported, when it closed. */
  bookBalance: z.number().int().nullable(),
  reportedBalance: z.number().int().nullable(),
  adjustment: z.string().nullable(),
  closedBy: z.string().nullable(),
  closedAt: z.iso.datetime().nullable(),
  /** Set when the deadline passed with it still open. */
  missedAt: z.iso.datetime().nullable(),
  version: z.number().int(),
});
export type Reconciliation = z.infer<typeof Reconciliation>;

/** A reported in-game balance: the whole balance for the primary account, the org's share for a held one. */
export const ReportInput = z.object({ balance: z.number().int().min(0).max(MAX_AMOUNT) });
export type ReportInput = z.input<typeof ReportInput>;

/** A reconciler's reconciliation at any time: Sierra's "Update balance", as an explicit adjustment. */
export const ReconcileNowInput = ReportInput.extend({ memo: z.string().trim().max(500).default('') });
export type ReconcileNowInput = z.input<typeof ReconcileNowInput>;

export const ClosePeriodInput = z.object({ note: z.string().trim().max(500).default('') });

/** What closing the period would do: a closing entry per account with a balance. */
export const ClosePreview = z.object({
  period: z.number().int(),
  accounts: z.array(Account),
  /** Entries still waiting: the close is refused until they're decided or cancelled. */
  waiting: z.number().int(),
  /** The most accounts one close can take (the unit of work's 100 operations). */
  max: z.number().int(),
});
export type ClosePreview = z.infer<typeof ClosePreview>;

/** "3 of 5 reconciled". */
export function reconciledCount(items: readonly Pick<Reconciliation, 'status'>[]): { done: number; total: number } {
  return { done: items.filter((item) => item.status === 'closed').length, total: items.length };
}

/** How many things are waiting on a user: the Bank nav badge (plan 2A Chunk 6). */
export const NeedsMe = z.object({
  /** All of the below. */
  count: z.number().int(),
  /** Pending entries they may approve: not theirs, within their approval limit. */
  approvals: z.number().int(),
  /** Transfers into accounts they hold, waiting for them to confirm. */
  transfers: z.number().int(),
  /** Payouts to them, not yet confirmed or disputed. */
  payouts: z.number().int(),
  /** Open reconciliations of accounts they hold. */
  reconciliations: z.number().int(),
});
export type NeedsMe = z.infer<typeof NeedsMe>;

/** What's waiting on one user (plan 2A Chunks 3 and 5, Mine). */
export const Mine = z.object({
  /** Entries they recorded that are still waiting for someone. */
  waiting: z.array(Entry),
  /** Transfers into accounts they hold, waiting for them to confirm. */
  toConfirm: z.array(Entry),
  /** Payouts to them this period, newest first. */
  payouts: z.array(Entry),
  /** Open reconciliations of accounts they hold. */
  reconciliations: z.array(Reconciliation),
  /** The names of those accounts, by id. */
  accounts: z.record(z.string(), z.string()),
  people: People,
});
export type Mine = z.infer<typeof Mine>;
